import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useMemo,
  useRef,
  useCallback,
} from "react";
import "./DiceCard.css";
import expIcon from "../../Assets/Images/dice-exp-icon.svg";
import Button from "../../Common/Button/Button";
import CustomTooltip from "../../Common/Tooltip/Tooltip";
import {
  applyEllipsis,
  toCapitalize,
  roundUpExperience,
} from "../../Common/Common-Function/CommonFunctions";
import ActiveCandidateIcon from "../../Assets/Images/active-candidate-icon.svg";
import { DynamicContext } from "../../Context/DynamicContext";
import LocationIcon from "../../Assets/Images/dice-location-icon.svg";
import VisaIcon from "../../Assets/Images/dice-visa-icon.svg";

const formatHourlyRate = (rate) => {
  if (!rate) return "Not specified";
  if (typeof rate === "object") {
    const min = Number(rate.min);
    const max = Number(rate.max);
    const currency = rate.currency || "USD";
    if (!isNaN(min) && min > 0 && !isNaN(max) && max > 0 && max !== min) {
      return `${min} - ${max} (${currency}) / hr`;
    }
    if (!isNaN(min) && min > 0) {
      return `${min} + (${currency}) / hr`;
    }
    if (!isNaN(max) && max > 0) {
      return `${max} + (${currency}) / hr`;
    }
    return "Not specified";
  }
  if (typeof rate === "number" && rate > 0) {
    return `${rate} + (USD) / hr`;
  }
  if (typeof rate === "string" && rate.trim()) {
    const trimmed = rate.trim();
    const num = Number(trimmed.replace(/[^0-9.]/g, ""));
    if (!isNaN(num) && num > 0) {
      return `${num} + (USD) / hr`;
    }
    return trimmed;
  }
  return "Not specified";
};

const formatAnnualSalary = (salary) => {
  if (!salary) return "No desired salary disclosed";
  if (typeof salary === "object") {
    const min = Number(salary.min);
    const max = Number(salary.max);
    const currency = salary.currency || "USD";
    if (!isNaN(min) && min > 0 && !isNaN(max) && max > 0 && max !== min) {
      return `${min.toLocaleString()} - ${max.toLocaleString()} (${currency}) / yr`;
    }
    if (!isNaN(min) && min > 0) {
      return `${min.toLocaleString()} + (${currency}) / yr`;
    }
    if (!isNaN(max) && max > 0) {
      return `${max.toLocaleString()} + (${currency}) / yr`;
    }
    return "No desired salary disclosed";
  }
  if (typeof salary === "number" && salary > 0) {
    return `${salary.toLocaleString()} + (USD) / yr`;
  }
  if (typeof salary === "string" && salary.trim()) {
    const trimmed = salary.trim();
    const num = Number(trimmed.replace(/[^0-9.]/g, ""));
    if (!isNaN(num) && num > 0) {
      return `${num.toLocaleString()} + (USD) / yr`;
    }
    return trimmed;
  }
  return "No desired salary disclosed";
};

const formatWillingToRelocate = (val) => {
  if (val === true || val === "true" || String(val).toLowerCase() === "yes") {
    return "Yes";
  }
  if (val === false || val === "false" || String(val).toLowerCase() === "no") {
    return "No";
  }
  return null;
};

const formatDetailDateTime = (dateVal) => {
  if (!dateVal) return "Not specified";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) {
    return typeof dateVal === "string" ? dateVal : "Not specified";
  }
  const pad = (n) => String(n).padStart(2, "0");
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const DiceCard = forwardRef((
  {
    name,
    role,
    experience,
    location,
    workPermitDocuments = [],
    skills = [],
    cardColor = "var(--header__color)",
    cardbackgroundColor = "var(--card__background)",
    skillColor = "var(--skill-badge-color)",
    skillbackgroundColor = "var(--skill-badge-bg)",
    cardWidth,
    viewEvent = () => "",
    showView = true,
    viewDisabled = false,
    cardBorder,
    cardView = "compact",
    isDetailed = false,
    showVisaStatus = false,
    dateLastActive = null,
    dateResumeLastUpdated = null,
    employmentType = [],
    annualSalary = null,
    hourlyRate = null,
    willingToRelocate = undefined,
  },
  ref
) => {
  const { isDarkMode } = useContext(DynamicContext);

  const isDetailedCard = isDetailed || cardView === "detailed";

  const workPermits = (
    Array.isArray(workPermitDocuments)
      ? workPermitDocuments
      : [workPermitDocuments]
  )
    .filter((document) => typeof document === "string" && document.trim())
    .map((document) => document.trim());

  const formattedLastActive = isDetailedCard ? formatDetailDateTime(dateLastActive) : null;
  const formattedResumeUpdated = isDetailedCard ? formatDetailDateTime(dateResumeLastUpdated) : null;

  const employmentTypesList = isDetailedCard
    ? (Array.isArray(employmentType) ? employmentType : [employmentType])
        .filter((type) => typeof type === "string" && type.trim())
        .map((type) => type.trim())
    : [];

  const formattedSalary = isDetailedCard ? formatAnnualSalary(annualSalary) : null;
  const formattedHourly = isDetailedCard ? formatHourlyRate(hourlyRate) : null;
  const formattedRelocate = isDetailedCard ? formatWillingToRelocate(willingToRelocate) : null;

  const skillsArray =
    typeof skills === "string"
      ? skills
          .split(",")
          .map((skill) => skill.trim())
          .filter((skill) => skill && skill.trim() !== "")
      : Array.isArray(skills)
        ? skills.filter((skill) => skill && skill.trim() !== "")
        : [];

  const internalCardRef = useRef(null);
  const [measuredCardWidth, setMeasuredCardWidth] = useState(0);

  const setCardRef = useCallback(
    (node) => {
      internalCardRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!internalCardRef.current) return;
    const updateSize = () => {
      if (internalCardRef.current) {
        setMeasuredCardWidth(internalCardRef.current.offsetWidth);
      }
    };
    updateSize();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect && entry.contentRect.width > 0) {
            setMeasuredCardWidth(entry.contentRect.width);
          }
        }
      });
      resizeObserver.observe(internalCardRef.current);
      return () => resizeObserver.disconnect();
    } else {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }
  }, []);

  const ellipsisLength = useMemo(() => {
    if (isDetailedCard) {
      return 60;
    }
    if (windowWidth > 1800) return 50;
    if (windowWidth <= 768 && windowWidth >= 425) return 30;
    if (windowWidth < 950) return 20;
    return 20;
  }, [isDetailedCard, windowWidth]);

  const visibleSkillsCount = useMemo(() => {
    if (isDetailedCard) {
      const effectiveWidth = measuredCardWidth > 0 ? measuredCardWidth : (windowWidth - 350);
      if (effectiveWidth >= 1350) return 9;
      if (effectiveWidth >= 1100) return 7;
      if (effectiveWidth >= 850) return 5;
      if (effectiveWidth >= 650) return 4;
      return 2;
    }
    return 2;
  }, [isDetailedCard, measuredCardWidth, windowWidth]);

  const displayedSkills = skillsArray.slice(0, visibleSkillsCount);
  const skillsToDisplay = isDetailedCard ? skillsArray : displayedSkills;

  return (
    <div
      className={`dice-card ${isDetailedCard ? "detailed-card" : ""}`}
      style={{
        color: cardColor,
        background: cardbackgroundColor,
        width: cardWidth,
        border: cardBorder,
        position: "relative",
      }}
      ref={setCardRef}
    >
      <div className="card-header-container">
        <div className="candidate-card-header">
          <div className="card-header-user">
            <div className="header-and-tags">
              <div className="card-header-text-container">
                <div className="candidate-icon">
                  <img
                    src={ActiveCandidateIcon}
                    alt="Candidate Icon"
                    className="card-candidate-icon"
                  />
                </div>
                <div className="card-header-text">
                  <h3>
                    {name && typeof name === "string" && name.trim()
                      ? applyEllipsis(toCapitalize(name), 30)
                      : "N/A"}
                  </h3>
                  {role && (
                    <div
                      className="card-details-span-candidate"
                      style={{ minWidth: 0, overflow: "hidden" }}
                    >
                      <CustomTooltip title={role} arrow>
                        <p className="card-subheader-candidate">
                          {applyEllipsis(role, ellipsisLength)}
                        </p>
                      </CustomTooltip>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="contact-info">
            <div className="card-exp-details-span">
              <img
                src={expIcon}
                alt="Experience"
                className="card-icon"
                style={{ width: "20px" }}
              />
              <p className="card-subheader-candidate">
                {roundUpExperience(experience)}
              </p>
            </div>
            {!isDetailedCard && location && (
              <div
                className="card-details-span-candidate"
                style={{ minWidth: 0, overflow: "hidden" }}
              >
                <img
                  src={LocationIcon}
                  alt="Location"
                  className="card-icon"
                  style={{ width: "20px" }}
                />
                <CustomTooltip title={location} arrow>
                  <p className="card-subheader-candidate">
                    {applyEllipsis(location, ellipsisLength)}
                  </p>
                </CustomTooltip>
              </div>
            )}
          </div>
        </div>
      </div>

      {isDetailedCard && (
        <div className="dice-detailed-info-card">
          <div className="dice-detail-item dice-detail-item-location">
            <span className="dice-detail-label"><img src={LocationIcon} alt="LocationIcon"/>LOCATION</span>
            <div className="dice-detail-value">
              {location && location !== "N/A" ? (
                <CustomTooltip title={location} arrow>
                  <span>{applyEllipsis(location, 50)}</span>
                </CustomTooltip>
              ) : (
                <span className="dice-detail-empty">Not specified</span>
              )}
            </div>
          </div>

          <div className="dice-detail-item">
            <span className="dice-detail-label"><img src={VisaIcon} alt="VisaIcon"/>VISA STATUS</span>
            <div className="dice-detail-value">
              {workPermits.length > 0 ? (
                workPermits.map((permit, index) => {
                  const isGreenCard = /green card/i.test(permit);
                  const isCitizen = /citizen/i.test(permit);
                  const badgeClass = isGreenCard
                    ? "dice-visa-green"
                    : isCitizen
                    ? "dice-visa-blue"
                    : "dice-visa-default";
                  return (
                    <span
                      className={`dice-visa-pill ${badgeClass}`}
                      key={`${permit}-${index}`}
                    >
                      <svg
                        className="dice-visa-shield-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                      {permit}
                    </span>
                  );
                })
              ) : (
                <span className="dice-detail-empty">Not specified</span>
              )}
            </div>
          </div>

          <div className="dice-detail-item">
            <span className="dice-detail-label">WILLING TO RELOCATE</span>
            <div className="dice-detail-value">
              {formattedRelocate ? (
                <span className="dice-relocate-wrap">
                  <span className="dice-relocate-bullet">•</span>
                  <span>{formattedRelocate}</span>
                </span>
              ) : (
                <span className="dice-detail-empty">Not specified</span>
              )}
            </div>
          </div>

          <div className="dice-detail-item">
            <span className="dice-detail-label">DESIRED ANNUAL SALARY</span>
            <div className="dice-detail-value">
              <span>{formattedSalary}</span>
            </div>
          </div>

          <div className="dice-detail-item">
            <span className="dice-detail-label">DESIRED HOURLY RATE</span>
            <div className="dice-detail-value">
              <span>{formattedHourly}</span>
            </div>
          </div>

          <div className="dice-detail-item">
            <span className="dice-detail-label">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              LAST ACTIVE
            </span>
            <div className="dice-detail-value">
              <span>{formattedLastActive}</span>
            </div>
          </div>

          <div className="dice-detail-item">
            <span className="dice-detail-label">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              LAST RESUME UPDATE
            </span>
            <div className="dice-detail-value">
              <span>{formattedResumeUpdated}</span>
            </div>
          </div>
        </div>
      )}

      {/* Employment Types Section (Placed above Skills) */}
      {isDetailedCard && (
        <div className="dice-card-employment-types-row">
          <span className="dice-detail-label">EMPLOYMENT TYPE:</span>
          <div className="dice-employment-row-pills">
            {employmentTypesList.length > 0 ? (
              employmentTypesList.map((type, index) => (
                <span className="dice-employment-pill" key={`${type}-${index}`}>
                  {type}
                </span>
              ))
            ) : (
              <span className="dice-detail-empty">Not specified</span>
            )}
          </div>
        </div>
      )}

      <div className="card-contact-info">
        <div className="skills-and-view-candidate-card">
          <div className="card-skills">
            <p>Skills</p>
            {skillsToDisplay.length > 0 ? (
              <div className="skills-container">
                {skillsToDisplay.map((skill, index) => (
                  <div
                    className="card-skills-div"
                    key={index}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      width: "auto",
                      maxWidth: "100%",
                      overflow: "visible",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      className="skill-badge"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "auto",
                        maxWidth: "none",
                        minWidth: "fit-content",
                        overflow: "visible",
                        textOverflow: "clip",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                      }}
                    >
                      {isDetailedCard
                        ? typeof skill === "string"
                          ? skill.trim()
                          : skill
                        : applyEllipsis(skill, 5)}
                    </span>
                  </div>
                ))}
                {!isDetailedCard && skillsArray.length > visibleSkillsCount && (
                  <CustomTooltip
                    className="skill-badge"
                    pill={false}
                    title={
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "5px",
                          maxWidth: "300px",
                          maxHeight: "100px",
                          overflowY: "auto",
                        }}
                      >
                        {skillsArray.map((skill, index) => (
                          <span
                            key={index}
                            className="skill-tooltip-badge"
                            style={{
                              color: skillColor,
                              background: skillbackgroundColor,
                            }}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    }
                    placement="top"
                    arrow
                  >
                    <div className="card-skills-div">
                      <span
                        className="skill-badge"
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        +{skillsArray.length - visibleSkillsCount}
                      </span>
                    </div>
                  </CustomTooltip>
                )}
              </div>
            ) : (
              <p className="no-skills">—</p>
            )}
          </div>
          <div className="view-container">
            {showView && (
              <Button
                label="View"
                event={viewEvent}
                disabled={viewDisabled}
                className="view"
                backgroundColor="none"
                color={isDarkMode ? "#aea9ff" : "#6860EE"}
                border={isDarkMode ? "1px solid #aea9ff" : "1px solid #6860EE"}
                style={{
                  padding: "4px 12px",
                  cursor: viewDisabled ? "not-allowed" : "pointer",
                  opacity: viewDisabled ? 0.5 : 1,
                  backgroundColor: "transparent",
                  color: isDarkMode ? "#aea9ff" : "#6860EE",
                  border: isDarkMode ? "1px solid #aea9ff" : "1px solid #6860EE",
                  borderRadius: "8px",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

DiceCard.displayName = "DiceCard";

export default DiceCard;
